import { ProductT, SkuT } from '@lambda/apis/src/catalog-v2/types';
import { ReebeloConditions } from '@lambda/reebelo';
import i18n from '@/settings/i18n';

const t = i18n.init('product');

/**
 * Alt text for each product photo: the selected variant's catalog title (so it
 * names the colour/storage being shown) plus the photo's position, so every
 * image in the gallery is described uniquely (RT-17692). Images carry no angle
 * tag yet, hence "View N". Brand-new stock is never called refurbished.
 */
export const buildProductImageAlt = ({
  title,
  brandName,
  condition,
  position,
}: {
  title: string;
  brandName?: string | null;
  condition?: string | null;
  position: number;
}) => {
  const productName =
    brandName && !title.toLowerCase().includes(brandName.toLowerCase())
      ? `${brandName} ${title}`
      : title;

  return condition === ReebeloConditions.BrandNew
    ? t`${{ productName }} - View ${{ position }}`
    : t`Refurbished ${{ productName }} - View ${{ position }}`;
};

export const generateCarouselImages = (
  selectedSku: SkuT | null,
  product: ProductT,
  isOutOfStock: boolean,
) => {
  if (isOutOfStock) {
    if (product?.images?.length) {
      return product.images;
    }

    if (product?.image) {
      return [product.image];
    }

    const skuImages = product?.skus?.flatMap((sku) => sku.images || []) || [];

    if (skuImages.length) {
      return skuImages;
    }

    return null;
  }

  if (selectedSku) {
    if (selectedSku.images && selectedSku.images.length > 0) {
      return selectedSku.images;
    }

    if (selectedSku.image) {
      return [selectedSku.image];
    }
  }

  return null;
};

export const generateOfferImage = (sku: SkuT | null, product: ProductT) =>
  sku?.images?.[0]?.url ??
  sku?.image?.url ??
  product.images?.[0]?.url ??
  product.image?.url ??
  '';

export const generateProductMultiImage = (
  sku: SkuT | null,
  product: ProductT,
) =>
  product.images?.[0]?.url ??
  product.image?.url ??
  sku?.images?.[0]?.url ??
  sku?.image?.url ??
  '';
